"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useMemo } from "react";
import { useCompetitionHomeData } from "../../../context/CompetitionHomeDataContext";
import { ArticlePyramid } from "../../../partials/articles/ArticlePyramid";
import { MatchCardList } from "../../../partials/matches/MatchCardList";
import { StandingsHorizontal } from "../../../partials/standings/StandingsHorizontal";
import { LeadersTables } from "../../../partials/players/LeadersTables";
import { SeasonStatsCards } from "../../../partials/stats/SeasonStatsCards";
import { PromoSlot } from "@/features/promo-slots/PromoSlot";
import { AdSlot } from "@/features/ads/AdSlot";
import { SectionStrip, type BrandedStyle } from "@/components/layout/SectionStrip";
import { HOME_HERO, HOME_STANDINGS, HOME_LEADERS, HOME_STATS } from "@/features/theme/sectionKeys";

/**
 * DataDashboardLayout — Skeleton Crew "at a glance" homepage.
 *
 * 1. Article Grid (3) + Match Card List sidebar
 * 2. Standings Horizontal (with points + played)
 * 3. Promo slot 0 + Promo slot 1 side-by-side
 * 4. Leaders (full tables)
 * 5. Season Stats
 */
interface Props {
  videoShortsSlot?: React.ReactNode;
  videoHighlightsSlot?: React.ReactNode;
  sectionStyles?: Record<string, BrandedStyle>;
}
export function FootballCompetitionHomeDataDashboardLayout(t0) {
  const $ = _c(58);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    videoShortsSlot,
    videoHighlightsSlot,
    sectionStyles
  } = t1;
  const {
    latestNews,
    featuredPosts,
    promos,
    leaders,
    standings,
    homeSlices,
    competitionStats,
    goalDistribution,
    hasCompetitionStats
  } = useCompetitionHomeData();
  let t2;
  if ($[2] !== standings?.entries) {
    t2 = standings?.entries ?? [];
    $[2] = standings?.entries;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const standingsEntries = t2;
  const t3 = featuredPosts?.length ? featuredPosts : latestNews;
  let t4;
  if ($[4] !== t3) {
    t4 = t3.slice(0, 3);
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const pyramidArticles = t4;
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <AdSlot id="content-top" />;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const t6 = sectionStyles?.[HOME_HERO];
  let t7;
  if ($[7] !== pyramidArticles) {
    t7 = <div className="lg:col-span-3"><ArticlePyramid articles={pyramidArticles} heroSizes="(max-width: 1024px) 100vw, 60vw" priority={true} /></div>;
    $[7] = pyramidArticles;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== homeSlices.lastFinished || $[10] !== homeSlices.nextUpcoming) {
    t8 = <div className="lg:col-span-2"><MatchCardList matches={[...homeSlices.nextUpcoming, ...homeSlices.lastFinished]} maxResults={6} maxUpcoming={4} /></div>;
    $[9] = homeSlices.lastFinished;
    $[10] = homeSlices.nextUpcoming;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t7 || $[13] !== t8) {
    t9 = <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">{t7}{t8}</div>;
    $[12] = t7;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t6 || $[16] !== t9) {
    t10 = <SectionStrip brandedStyle={t6}>{t9}</SectionStrip>;
    $[15] = t6;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <AdSlot id="mid-scroll-1" />;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const t12 = sectionStyles?.[HOME_STANDINGS];
  let t13;
  if ($[19] !== standingsEntries) {
    t13 = <StandingsHorizontal standings={standingsEntries} showPlayed={true} />;
    $[19] = standingsEntries;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== t12 || $[22] !== t13) {
    t14 = <SectionStrip brandedStyle={t12}>{t13}</SectionStrip>;
    $[21] = t12;
    $[22] = t13;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <AdSlot id="mid-scroll-2" />;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  const t16 = promos[0] ?? null;
  let t17;
  if ($[25] !== t16) {
    t17 = <PromoSlot row={t16} />;
    $[25] = t16;
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  const t18 = promos[1] ?? null;
  let t19;
  if ($[27] !== t18) {
    t19 = <PromoSlot row={t18} />;
    $[27] = t18;
    $[28] = t19;
  } else {
    t19 = $[28];
  }
  let t20;
  if ($[29] !== t17 || $[30] !== t19) {
    t20 = <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{t17}{t19}</div>;
    $[29] = t17;
    $[30] = t19;
    $[31] = t20;
  } else {
    t20 = $[31];
  }
  const t21 = promos[2] ?? null;
  let t22;
  if ($[32] !== t21) {
    t22 = <PromoSlot row={t21} />;
    $[32] = t21;
    $[33] = t22;
  } else {
    t22 = $[33];
  }
  const t23 = sectionStyles?.[HOME_LEADERS];
  let t24;
  if ($[34] !== leaders) {
    t24 = <LeadersTables leaders={leaders} />;
    $[34] = leaders;
    $[35] = t24;
  } else {
    t24 = $[35];
  }
  let t25;
  if ($[36] !== t23 || $[37] !== t24) {
    t25 = <SectionStrip brandedStyle={t23}>{t24}</SectionStrip>;
    $[36] = t23;
    $[37] = t24;
    $[38] = t25;
  } else {
    t25 = $[38];
  }
  let t26;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <AdSlot id="mid-scroll-3" />;
    $[39] = t26;
  } else {
    t26 = $[39];
  }
  const t27 = sectionStyles?.[HOME_STATS];
  let t28;
  if ($[40] !== competitionStats || $[41] !== goalDistribution || $[42] !== hasCompetitionStats || $[43] !== homeSlices.teamWithMostWins) {
    t28 = hasCompetitionStats && <SeasonStatsCards competitionStats={competitionStats} goalDistribution={goalDistribution} teamWithMostWins={homeSlices.teamWithMostWins} />;
    $[40] = competitionStats;
    $[41] = goalDistribution;
    $[42] = hasCompetitionStats;
    $[43] = homeSlices.teamWithMostWins;
    $[44] = t28;
  } else {
    t28 = $[44];
  }
  let t29;
  if ($[45] !== t27 || $[46] !== t28) {
    t29 = <SectionStrip brandedStyle={t27}>{t28}</SectionStrip>;
    $[45] = t27;
    $[46] = t28;
    $[47] = t29;
  } else {
    t29 = $[47];
  }
  let t30;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <AdSlot id="content-bottom" />;
    $[48] = t30;
  } else {
    t30 = $[48];
  }
  let t31;
  if ($[49] !== t10 || $[50] !== t14 || $[51] !== t20 || $[52] !== t22 || $[53] !== t25 || $[54] !== t29 || $[55] !== videoHighlightsSlot || $[56] !== videoShortsSlot) {
    t31 = <div className="min-h-screen bg-background"><main className="max-w-350 mx-auto px-4 py-6 space-y-10">{t5}{t10}{t11}{videoShortsSlot}{t14}{t15}{t20}{videoHighlightsSlot}{t22}{t25}{t26}{t29}{t30}</main></div>;
    $[49] = t10;
    $[50] = t14;
    $[51] = t20;
    $[52] = t22;
    $[53] = t25;
    $[54] = t29;
    $[55] = videoHighlightsSlot;
    $[56] = videoShortsSlot;
    $[57] = t31;
  } else {
    t31 = $[57];
  }
  return t31;
}